import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { ReactEditor } from 'slate-react';
import { useEditorRef } from 'platejs/react';

interface GhostSelectionOverlayProps {
    /** Slate-format Range snapshot from useAIMenu.savedSelection. */
    savedSelection: any | null;
}

// While the AI prompt is open the input grabs focus, which clears the
// browser's selection highlight on the editor — visually the user's
// selection looks gone, even though it's still preserved in the saved ref
// (and re-applied on submit). Re-paint a static highlight on top of the
// editor by walking the saved range with Range.getClientRects() and rendering
// semi-transparent overlay rects portalled to <body>.
//
// We re-measure on scroll / resize because the rects are viewport-fixed
// pixel positions — any layout shift moves the underlying text without
// notifying us. Cheap (a few rects per selection, simple style assignment).
export function GhostSelectionOverlay({ savedSelection }: GhostSelectionOverlayProps) {
    const editor = useEditorRef();
    const [rects, setRects] = useState<DOMRect[]>([]);

    useEffect(() => {
        if (!savedSelection) {
            setRects([]);
            return;
        }
        function compute() {
            try {
                const domRange = ReactEditor.toDOMRange(editor as any, savedSelection);
                const raw = Array.from(domRange.getClientRects()) as DOMRect[];

                // The overlay portals to <body> at z-index 10199 — above the
                // BottomDrawer (10100) and the sticky PlateToolbar hosted
                // inside it. If a selection scrolls behind the toolbar (or
                // outside the editor's content box), the raw client rects
                // would still paint there visually. Clip each rect to the
                // intersection of the editor content's bounds AND any sticky
                // toolbar sitting in the editor's top edge.
                const editorEl = document.querySelector('[data-testid="plate-editor-content"]') as HTMLElement | null;
                const editorBounds = editorEl?.getBoundingClientRect();
                const toolbarEl = document.querySelector('[role="toolbar"][aria-label="Editor toolbar"]') as HTMLElement | null;
                const toolbarBottom = toolbarEl?.getBoundingClientRect().bottom ?? -Infinity;

                const clipped = raw
                    .map((r) => {
                        if (!editorBounds) return r;
                        const top = Math.max(r.top, editorBounds.top, toolbarBottom);
                        const bottom = Math.min(r.bottom, editorBounds.bottom);
                        const left = Math.max(r.left, editorBounds.left);
                        const right = Math.min(r.right, editorBounds.right);
                        if (top >= bottom || left >= right) return null;
                        return new DOMRect(left, top, right - left, bottom - top);
                    })
                    .filter((r): r is DOMRect => r != null);

                setRects(clipped);
            } catch {
                // toDOMRange throws if the editor isn't mounted or the saved
                // range has drifted (rare — defensive only).
                setRects([]);
            }
        }
        compute();
        window.addEventListener('scroll', compute, true);
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('scroll', compute, true);
            window.removeEventListener('resize', compute);
        };
    }, [editor, savedSelection]);

    if (typeof document === 'undefined' || rects.length === 0) return null;

    return createPortal(
        <div aria-hidden style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10199 }}>
            {rects.map((r, i) => (
                <div
                    key={i}
                    style={{
                        position: 'fixed',
                        left: r.left,
                        top: r.top,
                        width: r.width,
                        height: r.height,
                        background: 'rgba(35, 131, 226, 0.22)',
                        borderRadius: 1,
                    }}
                />
            ))}
        </div>,
        document.body,
    );
}
